/** Match server `server/src/lib/timezone-schedule.ts` for virtual occurrence instants. */

export function formatWallTimeHHmmInTimezone(utcDate: Date, timezone: string): string {
  const { hour, minute } = getLocalDateParts(utcDate, timezone);
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function calendarDateKeyInTimezone(utcDate: Date, timezone: string): string {
  const { year, month, day } = getLocalDateParts(utcDate, timezone);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function addWallCalendarDays(
  year: number,
  month: number,
  day: number,
  delta: number,
  timezone: string
): { year: number; month: number; day: number } {
  if (delta === 0) return { year, month, day };
  const step = delta > 0 ? 1 : -1;
  let y = year;
  let m = month;
  let d = day;
  for (let i = 0; i < Math.abs(delta); i++) {
    const noon = localDateTimeToUTC(y, m, d, 12, 0, timezone);
    const next = new Date(noon.getTime() + step * 24 * 60 * 60 * 1000);
    const p = getLocalDateParts(next, timezone);
    y = p.year;
    m = p.month;
    d = p.day;
  }
  return { year: y, month: m, day: d };
}

export function getMondayOfWeekWallDateInTimezone(
  ref: Date,
  timezone: string
): { year: number; month: number; day: number } {
  const p = getLocalDateParts(ref, timezone);
  const daysSinceMonday = (p.dayOfWeek + 6) % 7;
  return addWallCalendarDays(p.year, p.month, p.day, -daysSinceMonday, timezone);
}

export function getLocalDateParts(
  utcDate: Date,
  timezone: string
): { year: number; month: number; day: number; dayOfWeek: number; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(utcDate);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || "";
  const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const hRaw = get("hour");
  const hour = hRaw === "24" ? 0 : parseInt(hRaw || "0", 10);
  const minute = parseInt(get("minute") || "0", 10);
  return {
    year: parseInt(get("year"), 10),
    month: parseInt(get("month"), 10),
    day: parseInt(get("day"), 10),
    dayOfWeek: dayMap[get("weekday")] ?? 0,
    hour,
    minute,
  };
}

export function localDateTimeToUTC(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timezone: string
): Date {
  const localStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`;
  const probe = new Date(localStr + "Z");
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const tzParts = formatter.formatToParts(probe);
  const g = (type: string) => tzParts.find((p) => p.type === type)?.value || "0";
  const h = g("hour") === "24" ? "00" : g("hour");
  const inTZ = new Date(`${g("year")}-${g("month")}-${g("day")}T${h}:${g("minute")}:${g("second")}Z`);
  const offsetMs = inTZ.getTime() - probe.getTime();
  return new Date(probe.getTime() - offsetMs);
}
